import { useEffect, useRef } from 'react';

/**
 * Runs a callback on an interval, pausing while the tab is hidden.
 *
 * Polling rather than websockets: the whole app is one operator watching for a
 * verification code, so a few seconds of latency is invisible and a socket
 * would be a reconnect-and-backoff problem to maintain for no gain.
 *
 * Pausing on hidden matters more than it looks. Without it, every tab left
 * open overnight keeps waking the device and writing to SQLite on the server
 * for nobody's benefit — and the refresh on becoming visible again means the
 * data is current by the time anyone actually looks at it.
 */
export function usePolling(callback: () => void, intervalMs: number, enabled: boolean): void {
  // Kept in a ref so that a caller passing a fresh closure on every render
  // does not tear down and restart the interval each time.
  const latest = useRef(callback);
  latest.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let timer: number | undefined;

    const stop = (): void => {
      if (timer !== undefined) window.clearInterval(timer);
      timer = undefined;
    };

    const start = (): void => {
      stop();
      timer = window.setInterval(() => latest.current(), intervalMs);
    };

    const onVisibilityChange = (): void => {
      if (document.hidden) {
        stop();
        return;
      }

      latest.current();
      start();
    };

    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled]);
}
